/* v3 - CSS animation infinite belt */
if (!customElements.get('share-space-reels')) {
  class ShareSpaceReels extends HTMLElement {
    constructor() {
      super();
      this.speed = parseFloat(this.getAttribute('data-speed') || '40');
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._init());
      });
    }

    _init() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.lightbox = this.querySelector('[data-lightbox]');
      this.lightboxVideo = this.querySelector('[data-lightbox-video]');
      this.lightboxClose = this.querySelector('[data-lightbox-close]');
      this.lightboxStrip = this.querySelector('[data-lightbox-strip]');

      const origCards = Array.from(this.track.children);
      if (origCards.length === 0) return;

      const gap = 16;
      const cardWidth = origCards[0].offsetWidth + gap;
      const totalOrigWidth = origCards.length * cardWidth;
      const viewportWidth = window.innerWidth;
      
      // Calculate how many times we need to clone to fill at least 2x viewport
      const minWidth = viewportWidth * 2;
      const clonesNeeded = Math.max(2, Math.ceil(minWidth / totalOrigWidth));
      
      // Clone the entire set multiple times
      for (let i = 0; i < clonesNeeded; i++) {
        origCards.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          this.track.appendChild(clone);
        });
      }

      // Animation should move by exactly the width of original cards
      const isMobile = window.innerWidth <= 768;
      const marqueeSpeed = isMobile ? this.speed / 1.25 : this.speed;
      const duration = totalOrigWidth / marqueeSpeed;
      this.track.style.setProperty('--marquee-duration', `${duration}s`);
      
      // Set the exact translate amount based on original width percentage
      const totalCards = origCards.length * (clonesNeeded + 1);
      const translatePercent = (origCards.length / totalCards) * 100;
      this.track.style.setProperty('--marquee-translate', `-${translatePercent}%`);

      this._bindVideoObserver();
      this._bindExpandButtons();
      this._bindLightbox();
    }

    _bindVideoObserver() {
      const videos = this.querySelectorAll('[data-reel-video]');
      if (!videos.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.3 }
      );

      videos.forEach((v) => observer.observe(v));
    }

    _bindExpandButtons() {
      this.querySelectorAll('[data-reel-expand]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const card = btn.closest('.sysv2-reels__card');
          const video = card.querySelector('[data-reel-video]');
          if (!video) return;
          const src = video.querySelector('source')?.src || video.src;
          this._openLightbox(src, card);
        });
      });
    }

    _openLightbox(src, card) {
      if (!this.lightbox || !this.lightboxVideo) return;

      const stripEl = this.lightboxStrip;
      const sourceStrip = card?.querySelector('.sysv2-reels__product-strip');
      if (stripEl) {
        const html = sourceStrip?.innerHTML?.trim() || '';
        if (html) {
          stripEl.innerHTML = html;
          stripEl.hidden = false;
        } else {
          stripEl.innerHTML = '';
          stripEl.hidden = true;
        }
      }

      this.lightboxVideo.src = src;
      this.lightbox.showModal();
      this.lightboxVideo.play().catch(() => {});
    }

    _bindLightbox() {
      if (!this.lightbox) return;

      this.lightboxClose?.addEventListener('click', () =>
        this._closeLightbox()
      );

      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) this._closeLightbox();
      });

      this.lightbox.addEventListener('close', () => {
        this.lightboxVideo.pause();
        this.lightboxVideo.src = '';
        if (this.lightboxStrip) {
          this.lightboxStrip.innerHTML = '';
          this.lightboxStrip.hidden = true;
        }
      });
    }

    _closeLightbox() {
      this.lightboxVideo.pause();
      this.lightbox.close();
    }

  }

  customElements.define('share-space-reels', ShareSpaceReels);
}

let sysv2LastHeadingOpen = 0;

/**
 * Theme editor / preview iframe only: programmatic <a target="_blank"> so the
 * click wins over section-selection handlers. Do not use `about:blank` +
 * `location.replace` — Chrome often leaves the tab stuck on `about:blank`.
 *
 * On a normal storefront tab we do not intercept; the real `<a>` in Liquid runs.
 */
function sysv2NavigateInstagramHeading(href) {
  const now = Date.now();
  if (now - sysv2LastHeadingOpen < 600) return;
  sysv2LastHeadingOpen = now;

  const a = document.createElement('a');
  a.href = href;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.setAttribute('referrerpolicy', 'no-referrer');
  a.style.cssText =
    'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0.01;pointer-events:auto;';
  document.body.appendChild(a);
  a.click();
  requestAnimationFrame(() => {
    try {
      a.remove();
    } catch (_) {}
  });
}

/**
 * One document-level capture listener runs before Shopify theme-editor handlers
 * that stop propagation on the preview iframe.
 */
function sysv2BindReelsHeadingHandleNav() {
  if (window.__sysv2ReelsHeadingNavBound) return;
  window.__sysv2ReelsHeadingNavBound = true;

  const handle = (e) => {
    if (e.defaultPrevented) return;
    const anchor = e.target?.closest?.('a.sysv2-reels__heading-handle');
    if (!anchor) return;

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.type === 'mousedown' && e.button !== 0) return;

    const href = anchor.getAttribute('href')?.trim();
    if (!href || href === '#' || href.toLowerCase().startsWith('javascript:')) return;

    const inEditor =
      typeof Shopify !== 'undefined' &&
      typeof Shopify.designMode !== 'undefined' &&
      Shopify.designMode;
    const inIframe = window.self !== window.top;

    /* Real customers: native link — no JS navigation (avoids blank tabs / COOP quirks). */
    if (!inEditor && !inIframe) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    sysv2NavigateInstagramHeading(href);
  };

  const handleKey = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    handle(e);
  };

  /* mousedown runs before many editor "select section" click handlers */
  document.addEventListener('mousedown', handle, true);
  document.addEventListener('click', handle, true);
  document.addEventListener('keydown', handleKey, true);
}

sysv2BindReelsHeadingHandleNav();
